import React from 'react';
import clsx from 'clsx';
import { ChevronDown, ChevronRight, Users, Plus, Minus } from 'lucide-react';
import { Checkbox } from '@components/common';
import { ScheduleItem, ScheduleItemContract } from '@app-types';
import { STATUS_LABELS } from '@utils/statusUtils';
import { kebabToPascalCase } from '@utils/stringUtils';
import styles from './SchedulerTableCells.module.css';

export type StateCellParams = {
  scheduleItem: ScheduleItem;
  contract: ScheduleItemContract;
  isGroupRow: boolean;
  isChildRow: boolean;
  isCheckboxDisabled?: boolean;
  isSelected?: boolean;
  isExpanded?: boolean;
  onToggleSelection?: (itemId: number | null, contractId: number, item?: ScheduleItem) => void;
  onToggleExpand?: (itemId: number) => void;
  onAddToGroup?: (item: ScheduleItem) => void;
  onRemoveFromGroup?: (groupId: number, contractId: number) => void;
};

// Helper: Get state information for a contract
const getStateInfo = (contract: ScheduleItemContract) => {
  const stateValue = contract.state || 'pending_data_enrichment';
  const stateClass = stateValue.replace(/_/g, '-');
  const stateLabel = STATUS_LABELS[stateValue] || stateValue;
  const statusClassKey = `status${kebabToPascalCase(stateClass)}` as keyof typeof styles;

  return { stateValue, stateClass, stateLabel, statusClassKey };
};

// Helper: Render state badge
const renderStateBadge = (stateLabel: string, statusClassKey: keyof typeof styles): React.ReactElement => {
  return (
    <div className={styles.stateCell}>
      <span className={clsx(styles.statusBadge, styles[statusClassKey])}>{stateLabel}</span>
    </div>
  );
};

// Helper: Check if meeting is scheduled or rescheduled
const isMeetingScheduled = (meeting?: { status?: string } | null): boolean => {
  return meeting?.status === 'scheduled' || meeting?.status === 'rescheduled';
};

// Helper: Render group parent row state
export const renderGroupParentState = (
  scheduleItem: ScheduleItem,
  isCheckboxDisabled: boolean | undefined,
  isExpanded: boolean | undefined,
  onToggleExpand?: (itemId: number) => void,
  onAddToGroup?: (item: ScheduleItem) => void
): React.ReactElement => {
  return (
    <div className={styles.statusCellContent}>
      <button
        className={styles.expandButton}
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpand?.(scheduleItem.id!);
        }}
        title={isExpanded ? 'Collapse' : 'Expand'}
        data-test-id="expand-button"
      >
        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      <span className={styles.groupBadge}>
        <Users size={14} />
        {scheduleItem.contracts.length} contracts
      </span>
      {!isCheckboxDisabled && !isMeetingScheduled(scheduleItem.meeting) && (
        <button
          className={styles.addToGroupButton}
          data-test-id="add-to-group-button"
          onClick={(e) => {
            e.stopPropagation();
            onAddToGroup?.(scheduleItem);
          }}
          title="Add contracts to this group"
        >
          <Plus size={14} />
        </button>
      )}
    </div>
  );
};

// Helper: Render child row state
export const renderChildRowState = (
  scheduleItem: ScheduleItem,
  contract: ScheduleItemContract,
  isCheckboxDisabled: boolean | undefined,
  onRemoveFromGroup?: (groupId: number, contractId: number) => void
): React.ReactElement => {
  const { stateLabel, statusClassKey } = getStateInfo(contract);

  // If checkboxes disabled, only show state badge
  if (isCheckboxDisabled) {
    return renderStateBadge(stateLabel, statusClassKey);
  }

  // Otherwise show remove button + state badge
  return (
    <div className={styles.childStatusContent}>
      {!isMeetingScheduled(scheduleItem.meeting) && (
        <button
          className={styles.removeFromGroupButton}
          onClick={(e) => {
            e.stopPropagation();
            onRemoveFromGroup?.(scheduleItem.id!, contract.id);
          }}
          title="Remove from group"
        >
          <Minus size={14} />
        </button>
      )}
      {renderStateBadge(stateLabel, statusClassKey)}
    </div>
  );
};

// Helper: Render single row state
export const renderSingleRowState = (
  scheduleItem: ScheduleItem,
  contract: ScheduleItemContract,
  isCheckboxDisabled: boolean | undefined,
  isSelected: boolean | undefined,
  onToggleSelection?: (itemId: number | null, contractId: number, item?: ScheduleItem) => void
): React.ReactElement => {
  const { stateLabel, statusClassKey } = getStateInfo(contract);

  // If checkboxes disabled, only show state badge
  if (isCheckboxDisabled) {
    return renderStateBadge(stateLabel, statusClassKey);
  }

  // Otherwise show checkbox + state badge
  return (
    <div className={styles.statusCellContent}>
      <Checkbox
        checked={isSelected || false}
        onChange={() => onToggleSelection?.(scheduleItem.id, contract.id, scheduleItem)}
        onClick={(e) => e.stopPropagation()}
        dataTestId={`scheduler-row-${contract.id}-checkbox`}
      />
      {renderStateBadge(stateLabel, statusClassKey)}
    </div>
  );
};
